import {
  Fragment,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Stack, Button, Box } from '@mui/material';
import Alert from '../../common/Alert.component';
import { Form } from '../../form/Form';
import DrawerComponent from '../../common/Drawer.component';

const ModalSendOfBase = ({
  children,
  conf,
  button,
  onSubmitAction,
  onCloseSuccess = () => {},
  withForm = true,
  submitLabel = 'Confirm',
  loadingLabel = 'Sending...',
  successMessage = 'Success',
  defaultValues = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const { fields = [], schema } = conf || {};
  const [formDefaultValues, setFormDefaultValues] = useState(defaultValues);

  const handleOpen = useCallback(() => {
    setOpen(true);
    setSuccess(false);
    setError(null);
  }, []);

  const customButton = useMemo(() => {
    if (!button || !isValidElement(button)) {
      return button;
    }

    const originalOnClick = button.props?.onClick;

    return cloneElement(button, {
      onClick: (event) => {
        handleOpen();
        if (originalOnClick) {
          originalOnClick(event);
        }
      },
    });
  }, [button, handleOpen]);

  const onError = useCallback((submitError) => {
    setError(submitError);
    setLoading(false);
  }, []);

  const onSuccess = useCallback(() => {
    setSuccess(true);
    setLoading(false);
  }, []);

  const runSubmit = useCallback(
    (values = {}) => {
      if (!onSubmitAction) {
        return;
      }

      setError(null);
      setSuccess(false);
      setLoading(true);

      Promise.resolve(onSubmitAction(values)).then(onSuccess).catch(onError);
    },
    [onSubmitAction, onSuccess, onError]
  );

  const onSubmit = useCallback(
    (values) => {
      runSubmit(values);
    },
    [runSubmit]
  );

  const onClose = useCallback(() => {
    if (success) {
      onCloseSuccess();
    }
  }, [success, onCloseSuccess]);

  useEffect(() => {
    if (open && withForm) {
      setFormDefaultValues(defaultValues);
    }
  }, [open, withForm, defaultValues]);

  return (
    <Fragment>
      {success && <Alert severity='success'>{successMessage}</Alert>}
      {error && (
        <Alert severity='error'>
          {error?.message ? error.message : 'An error has occurred'}
        </Alert>
      )}

      <DrawerComponent onClose={onClose} button={customButton}>
        <Stack spacing={2} justifyContent='center' alignItems='stretch' width={1}>
          {withForm ? (
            <Form
              fields={fields}
              schema={schema}
              defaultValues={formDefaultValues}
              onSubmit={onSubmit}
            >
              {children}

              <Box display='flex' justifyContent='flex-end' alignItems='center' width={1}>
                <Button
                  type='submit'
                  variant='contained'
                  disabled={loading || success}
                  autoFocus
                >
                  {loading ? loadingLabel : submitLabel}
                </Button>
              </Box>
            </Form>
          ) : (
            <>
              {children}
              <Box display='flex' justifyContent='flex-end' alignItems='center' width={1}>
                <Button
                  onClick={() => runSubmit({})}
                  variant='contained'
                  disabled={loading || success}
                  autoFocus
                >
                  {loading ? loadingLabel : submitLabel}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </DrawerComponent>
    </Fragment>
  );
};

export default ModalSendOfBase;
